import { insertartBitacoraCliente } from "../clientes/thunkClientes";
import { insertartBitacoraEmpresa } from "../Empresa/thunkEmpresa";
import {
    cambioEstadoCotizacionApi,
    crearCotizacionApi,
    deleteEliminarCotizacion,
    getCotizacionApi,
    obtener_CotizacionId,
    obtener_CotizacionIdCliente,
    obtener_NumeroConsecutivo,
    obtenerLista_Cotizaciones,
    obtenerSalonesSegunHotel,
} from "./Api_Cotizaciones_Providers";

/**
 * Obtiene la lista paginada de cotizaciones desde la API.
 *
 * @param {Object} query - Filtros y paginación.
 * @returns {Promise<Object>} Respuesta completa lista para la vista.
 */
export const obtenerListaCotizaciones = (query) => {
    return async () => {
        try {
            const resultado = await obtenerLista_Cotizaciones(query);
            return resultado.data;
        } catch (error) {
            console.error("Error al obtener la lista de cotizaciones:", error);
        }
    };
};

export const obtenerCotizacionId = (idConsecutivo) => {
    return async () => {
        try {
            const resultado = await obtener_CotizacionId({ idConsecutivo });
            return resultado.data.data["0"];
        } catch (error) {
            console.error("Error al obtener la cotización por ID:", error);
        }
    };
};

/**
 * Acción para obtener el número consecutivo desde la API.
 *
 * @returns {Promise<Object>} Primer registro con consecutivo.
 */
export const obtenerNumeroConsecutivo = () => {
    return async () => {
        try {
            const respuesta = await obtener_NumeroConsecutivo();
            return respuesta.data.data[0];
        } catch (error) {
            console.error("Error al obtener el número consecutivo:", error);
            throw new Error("No se pudo obtener el número consecutivo");
        }
    };
};

/**
 * Acción para obtener salones desde la API.
 *
 * @param {Object} idHotel - Hotel objetivo.
 * @returns {Promise<Array>} Lista de salones.
 */
export const traerSalones = (idHotel) => {
    return async () => {
        try {
            const respuesta = await obtenerSalonesSegunHotel(idHotel);
            return respuesta.data.data;
        } catch (error) {
            console.error("Error al obtener los salones:", error);
            throw new Error("No se pudo obtener los salones");
        }
    };
};

/**
 * Acción para crear una cotización.
 *
 * @param {Object} formulario - Datos del formulario.
 * @param {Array} fechas - Fechas asociadas.
 * @param {boolean} mostrarCalendario - Estado del checkbox.
 * @returns {Promise<Object>} Respuesta de API.
 */
export const crearCotizacion = (formulario, fechas, mostrarCalendario) => {
    return async (dispatch, getState) => {
        const { id_admin } = getState().auth;

        try {
            const datosCompletos = {
                formulario,
                fechas,
                id_admin,
                mostrarCalendario,
            };

            const respuesta = await crearCotizacionApi({ datosCompletos });

            if (formulario.datosCliente.id_cliente > 0) {
                await dispatch(
                    insertartBitacoraCliente({
                        bitacora_cliente: formulario.datosCliente.id_cliente,
                        bitacora_nombreCliente: "Cotizacion creada",
                        bitacora_detalle: `Se le ha generado una Cotizacion para este cliente Cotizacion #${formulario.consecutivo} , Nombre Cotizacion: ${formulario.nombreEvento}`,
                    }),
                );
            }

            if (formulario.empresaSeleccionada > 0) {
                const nota = `Se le ha generado una Cotizacion para esta empresa Cotizacion #${formulario.consecutivo} , Nombre Cotizacion: ${formulario.nombreEvento}`;
                const idEmpresa = formulario.empresaSeleccionada;
                await dispatch(insertartBitacoraEmpresa(nota, idEmpresa));
            }

            const { data } = respuesta;
            return data;
        } catch (error) {
            console.error("Error al crear la cotización:", error.message || error);
            throw new Error("No se pudo crear la cotización. Verifique los datos proporcionados.");
        }
    };
};

/**
 * Acción para obtener una cotización desde la API.
 *
 * @param {string|number} id - ID interno de cotización.
 * @returns {Promise<Object>} Datos de cotización.
 */
export const getCotizacion = (id) => {
    return async () => {
        try {
            const respuesta = await getCotizacionApi({ id });
            return respuesta.data;
        } catch (error) {
            console.error("Error al obtener la cotización:", error);
            throw new Error("No se pudo obtener la cotización");
        }
    };
};

/**
 * Acción para eliminar una cotización mediante la API.
 *
 * @param {string|number} id - Consecutivo a eliminar.
 * @returns {Promise<Object>} Respuesta de API.
 */
export const eliminarCotizacion = (id) => {
    return async () => {
        try {
            const respuesta = await deleteEliminarCotizacion({ id });
            return respuesta.data;
        } catch (error) {
            console.error("Error al eliminar la cotización:", error);
            throw new Error("No se pudo eliminar la cotización");
        }
    };
};

/**
 * Reemplaza una cotización eliminando la anterior y creando la nueva versión.
 *
 * @param {Object} formulario - Datos editados.
 * @param {Array} fechas - Fechas del evento.
 * @param {boolean} mostrarCalendario - Estado del checkbox.
 * @param {string|number} id_admin - Admin editor.
 * @param {string|number} ids - Identificador previo.
 * @returns {Promise<Object>} Respuesta de API.
 */
export const editarCotizacion = (formulario, fechas, mostrarCalendario, id_admin, ids) => {
    return async (dispatch) => {
        try {
            const datosCompletos = {
                formulario,
                fechas,
                id_admin,
                mostrarCalendario,
            };

            const id = formulario.consecutivo;
            await dispatch(eliminarCotizacion(id));

            const respuesta2 = await crearCotizacionApi({ datosCompletos });
            const { data } = respuesta2;

            return data;
        } catch (error) {
            console.error("Error al editar la cotización:", error.message || error);
            throw new Error("No se pudo editar la cotización. Verifique los datos proporcionados.");
        }
    };
};

/**
 * Cambia estado de una o varias cotizaciones.
 *
 * @param {Array|string|number} ids - IDs a modificar.
 * @param {Array|string} estadosRelacionados - Estados relacionados.
 * @param {number|string} value - Nuevo valor.
 * @param {string} token - Token de evento.
 * @returns {Promise<Object>} Respuesta de API.
 */
export const cambioEstadoCotizacion = (ids, estadosRelacionados, value, token) => {
    return async () => {
        try {
            const respuesta2 = await cambioEstadoCotizacionApi({ ids, estadosRelacionados, value, token });
            const { data } = respuesta2;
            return data;
        } catch (error) {
            console.error("Error al cambiar el estado de la cotización:", error.message || error);
            throw new Error("No se pudo cambiar el estado de la cotización. Verifique los datos proporcionados.");
        }
    };
};

/**
 * Extrae cotizaciones ligadas a un cliente.
 *
 * @param {string|number} dato - ID cliente.
 * @returns {Promise<Array>} Lista de cotizaciones.
 */
export const extarerCotizacionesCliente = (dato) => {
    return async () => {
        try {
            const resultado = await obtener_CotizacionIdCliente({ dato });
            return resultado.data.data;
        } catch (error) {
            console.error("Error al obtener la cotización por ID:", error);
        }
    };
};
